import { apiDefinition } from './definitions';
import { credReqError } from '../errorHandlers/credReqError';
import { PageRes } from '$shared/models/api/PageRes';
import type { IPageRes } from '$shared/interfaces/api/IPageRes';
import type { ICredReq } from '$shared/interfaces/api/ICredReq';
import type { IPatchCredReq } from '$shared/interfaces/api/IPatchCredReq';
import type { CredReqListFilter } from '$shared/models/CredReqListFilter';
import { CredReq } from '$shared/models/credReq/CredReq';
import type { ICountryModel } from '$shared/interfaces/api/ICountryModel';
import { CountryModel } from '$shared/models/CountryModel';
import { authError } from '../errorHandlers/authError';
import User from '$shared/models/User';
import type { IUser } from '$shared/interfaces/api/IUser';
import type { IPasswordAuth } from '$shared/interfaces/api/IPasswordAuth';
import Authorization from '$shared/models/Authorization';
import type { AuthTokenSort } from '$shared/constants/AuthTokenSort';
import SortDirection from '$shared/constants/SortDirection';
import AuthToken from '$shared/models/api/AuthToken';
import type { IAuthToken } from '$shared/interfaces/api/IAuthToken';
import type { IGoogleAuth } from '$shared/interfaces/api/IGoogleAuth';
import type { IForgotPassword } from '$shared/interfaces/api/IForgotPassword';
import type { IResetPassword } from '$shared/interfaces/api/IResetPassword';
import type { IOAuthModel } from '$shared/interfaces/api/IOAuthModel';
import UserRole from '$shared/models/UserRole';
import type { Role } from '$shared/constants/Role';
import { datasetError } from '../errorHandlers/datasetError';
import type { DatasetScanListFilter } from '$shared/models/DatasetScanListFilter';
import { DatasetScan } from '$shared/models/dataset/DatasetScan';
import type { IDatasetScan } from '$shared/interfaces/api/IDatasetScan';
import type { DatasetMatchListFilter } from '$shared/models/DatasetMatchListFilter';
import { DatasetMatch } from '$shared/models/dataset/DatasetMatch';
import type { IStartDatasetScan } from '$shared/interfaces/api/IStartDatasetScan';
import { VerificationMetadata } from '$shared/models/VerificationMetadata';
import type { IVerificationMetadata } from '$shared/interfaces/api/IVerificationMetadata';
import { organizationError } from '../errorHandlers/organizationError';
import OrgSettings from '$shared/models/OrgSettings';
import type { IOrgSettings } from '$shared/interfaces/api/IOrgSettings';
import { schemaError } from '../errorHandlers/schemaError';
import { SchemaMeta } from '$shared/models/SchemaMeta';
import type { ISchemaMeta } from '$shared/interfaces/api/ISchemaMeta';
import type { IFile } from '$shared/interfaces/IFile';
import { XFile } from '$shared/models/XFile';
import { fileError } from '../errorHandlers/fileError';
import type { FileListFilter } from '$shared/models/FileListFilter';
import type ApiError from '$shared/models/api/ApiError';
import CacheControl from '$shared/constants/CacheControl';
import { monitorError } from '../errorHandlers/monitorError';
import type { IMonitorReq } from '$shared/interfaces/api/IMonitorReq';
import type { IDatasetMatch } from '$shared/interfaces/api/IDatasetMatch';
import type { IDatasetMatchPatchRequest } from '$shared/interfaces/api/IDatasetMatchPatchRequest';
import { Monitor } from '$shared/models/monitor/Monitor';
import type { IMonitor } from '$shared/interfaces/api/IMonitor';
import type { IMonitorPatchReq } from '$shared/interfaces/api/IMonitorPatchReq';
import type { MonitorListFilter } from '$shared/models/MonitorListFilter';
import type { DatasetMetaListFilter } from '$shared/models/DatasetMetaListFilter';
import DatasetMeta from '$shared/models/DatasetMeta';
import type { IDatasetMeta } from '$shared/interfaces/api/IDatasetMeta';
import { MonitorFactory } from '$shared/factory/MonitorFactory';
import { credentialingRequestError } from '../errorHandlers/credentialingRequestError';
import type { ICheckList } from '$shared/interfaces/api/ICheckList';
import type { IAttachCredReqEvent } from '$shared/interfaces/api/IAttachCredReqEvent';
import type { ModuleEntityType } from '$shared/constants/ModuleEntityType';
import { CvoAggregation } from '$shared/models/CvoAggregation';
import type { IAuditLog } from '$shared/interfaces/api/IAuditLog';
import AuditLog from '$shared/models/audit/AuditLog';
import type { AuditLogListFilter } from '$shared/models/filter/AuditLogListFilter';
import type { DownloadLogsFilter } from '$shared/models/filter/DownloadLogsFilter';
import DownloadLogs from '$shared/models/audit/DownloadLogs';
import type { IDownloadLog } from '$shared/interfaces/api/IDownloadLog';
import { auditError } from '../errorHandlers/auditError';
import type { IOAuthCredentialsModel } from '$shared/interfaces/api/IOAuthCredentialsModel';
import type { IClientSecretModel } from '$shared/interfaces/api/IClientSecretModel';
import ApiErrorWrapper from '$shared/helpers/decorators/ApiErrorWrapper';
import type { LicenseTypeStatusListFilter } from '$shared/models/filter/LicenseTypeStatusListFilter';
import { SimplifiedLicenseTypeStatusModel } from '$shared/models/SimplifiedLicenseTypeStatusModel';
import type { ISimplifiedLicenseTypeStatus } from '$shared/interfaces/api/ISimplifiedLicenseTypeStatus';
import { Segment } from '$shared/models/Segment';
import type { PageFilter } from '$shared/models/PageFilter';
import type { TaxonomyGroupSort } from '$shared/constants/TaxonomyGroupSort';
import type { ITaxonomyGroup } from '$shared/interfaces/api/ITaxonomyGroup';
import { TaxonomyGroup } from '$shared/models/TaxonomyGroup';
import { TaxonomyModel } from '$shared/models/TaxonomyModel';
import type { ITaxonomy } from '$shared/interfaces/api/ITaxonomy';
import type { TaxonomySort } from '$shared/constants/TaxonomySort';
import type { ICreateCredReq } from '$shared/interfaces/api/ICreateCredReq';
import type { Settings } from '$shared/constants/Settings';
import { SettingsFactory } from '$shared/factory/SettingsFactory';

class Api {
  @ApiErrorWrapper(authError.passwordAuth())
  public async authPasswordAsync(authInfo: IPasswordAuth): Promise<Authorization> {
    return Authorization.create(await apiDefinition.authPasswordAsync(authInfo));
  }

  @ApiErrorWrapper(authError.googleAuth())
  public async authGoogleAsync(authInfo: IGoogleAuth): Promise<Authorization> {
    return Authorization.create(await apiDefinition.authGoogleAsync(authInfo));
  }

  @ApiErrorWrapper(authError.googleAuth())
  public async credAuthGoogleAsync(authInfo: IGoogleAuth): Promise<Authorization> {
    return Authorization.create(await apiDefinition.credAuthGoogleAsync(authInfo));
  }

  @ApiErrorWrapper(authError.googleAuth())
  public async consoleAuthGoogleAsync(authInfo: IGoogleAuth): Promise<Authorization> {
    return Authorization.create(await apiDefinition.consoleAuthGoogleAsync(authInfo));
  }

  @ApiErrorWrapper(authError.forgotPassword())
  public async forgotPasswordAsync(authInfo: IForgotPassword): Promise<void> {
    await apiDefinition.forgotPasswordAsync(authInfo);
  }

  @ApiErrorWrapper(authError.resetPassword())
  public async resetPasswordAsync(authInfo: IResetPassword): Promise<void> {
    await apiDefinition.resetPasswordAsync(authInfo);
  }

  @ApiErrorWrapper(organizationError.getSsoAdminLink())
  public async getSSOAdminLinkAsync(code: string): Promise<{ adminPortalLink: string }> {
    return await apiDefinition.getSSOAdminLinkAsync(code);
  }

  @ApiErrorWrapper(authError.invalidateToken())
  public async invalidateAccessTokenAsync(tokenId: string): Promise<void> {
    await apiDefinition.invalidateAccessTokenAsync(tokenId);
  }

  @ApiErrorWrapper(authError.invalidateToken())
  public async invalidateCredAccessTokenAsync(tokenId: string): Promise<void> {
    await apiDefinition.invalidateCredAccessTokenAsync(tokenId);
  }

  @ApiErrorWrapper(authError.oAuth())
  public async authOAuthAsync(authInfo: IOAuthModel): Promise<Authorization> {
    return Authorization.create(await apiDefinition.authOAuthAsync(authInfo));
  }

  @ApiErrorWrapper(authError.changePassword())
  public async changePasswordAsync(authInfo: IPasswordAuth): Promise<Authorization> {
    return Authorization.create(await apiDefinition.authPasswordAsync(authInfo));
  }

  @ApiErrorWrapper(authError.listTokens())
  public async getTokensAsync(
    sortBy?: AuthTokenSort,
    cursor?: string,
    sortDirection?: SortDirection
  ): Promise<PageRes<AuthToken>> {
    const response: IPageRes<IAuthToken> = await apiDefinition.getTokensAsync(
      sortBy,
      cursor,
      sortDirection
    );

    return PageRes.create<AuthToken, IAuthToken>(response, (tokenRes) =>
      AuthToken.fromResponse(tokenRes)
    );
  }
  //#endregion

  //#region User Role
  @ApiErrorWrapper()
  public async getOrganizationRoleAsync(role: Role): Promise<UserRole> {
    return UserRole.from(await apiDefinition.getOrganizationRoleAsync(role));
  }
  //#endregion

  //#region Credentialing Requests
  @ApiErrorWrapper(authError.credUserList())
  public async getCredUsersAsync(): Promise<User[]> {
    return (await apiDefinition.getCredUsersAsync()).map((user: IUser) => new User(user));
  }

  @ApiErrorWrapper(credReqError.get())
  public async getCredentialingRequestAsync(id: string): Promise<CredReq> {
    return new CredReq(await apiDefinition.getCredentialingRequestAsync(id));
  }

  @ApiErrorWrapper(credReqError.create())
  public async createCredentialingRequestAsync(payload: ICreateCredReq): Promise<CredReq> {
    return new CredReq(await apiDefinition.createCredentialingRequestAsync(payload));
  }

  @ApiErrorWrapper(credReqError.patch())
  public async updateCredentialingRequestAsync(
    id: string,
    payload: IPatchCredReq
  ): Promise<CredReq> {
    return new CredReq(await apiDefinition.updateCredentialingRequestAsync(id, payload));
  }

  @ApiErrorWrapper(credReqError.attachCredEvent())
  public async attachCredReqEventAsync(id: string, payload: IAttachCredReqEvent): Promise<CredReq> {
    return new CredReq(await apiDefinition.attachCredReqEventAsync(id, payload));
  }

  @ApiErrorWrapper(credReqError.getList())
  public async getCredentialingRequestListAsync(
    filter: CredReqListFilter
  ): Promise<PageRes<CredReq>> {
    const response: IPageRes<ICredReq> = await apiDefinition.getCredentialingRequestListAsync(
      filter.entityType,
      filter.currentEventStatus,
      filter.providerCredentialingStatus,
      filter.priority,
      filter.ownerId,
      filter.includeUnassigned,
      filter.credentialingRequestType,
      filter.entityId,
      filter.entityName,
      filter.entityNpi,
      filter.entityDeactivated,
      filter.facilitySpecialtyId,
      filter.sortedBy,
      filter.sort,
      filter.cursor,
      filter.count,
      filter.sortDirection
    );

    return PageRes.create<CredReq, ICredReq>(response, (credReq: ICredReq) => new CredReq(credReq));
  }

  @ApiErrorWrapper(credentialingRequestError.updateChecklist())
  public async updateChecklistAsync(credReqId: string, payload: ICheckList): Promise<void> {
    await apiDefinition.updateChecklistAsync(credReqId, payload);
  }
  //#endregion

  //#region Segments
  @ApiErrorWrapper()
  public async getSegmentListAsync(): Promise<Segment[]> {
    return (
      (await apiDefinition.getSegmentListAsync())?.map((segment) => Segment.from(segment)) || []
    );
  }
  //#endregion

  //#region Countries
  @ApiErrorWrapper()
  public async getCountriesListAsync(): Promise<CountryModel[]> {
    return (await apiDefinition.getCountriesListAsync()).map(
      (item: ICountryModel) => new CountryModel(item)
    );
  }
  //#endregion

  //#region User
  @ApiErrorWrapper(authError.userList())
  public async getUsersAsync(): Promise<User[]> {
    return (await apiDefinition.getUsersAsync()).map((user: IUser) => new User(user));
  }
  //#endregion

  //#region Dataset
  @ApiErrorWrapper(datasetError.getScanList())
  public async getDatasetScansListAsync(
    filter: DatasetScanListFilter
  ): Promise<PageRes<DatasetScan>> {
    const response: IPageRes<IDatasetScan> = await apiDefinition.getDatasetScansListAsync(
      filter.type,
      filter.parameterSetId,
      filter.referenceId,
      filter.status,
      filter.includeOldScans,
      filter.hasMonitor,
      filter.trigger,
      filter.entityType,
      filter.entityId,
      filter.entityName,
      filter.entityNpi,
      filter.entityDeactivated,
      filter.credentialingStatus,
      filter.sortedBy,
      filter.sort,
      filter.cursor,
      filter.count,
      filter.sortDirection
    );

    return PageRes.create<DatasetScan, IDatasetScan>(
      response,
      (scan: IDatasetScan) => new DatasetScan(scan)
    );
  }

  @ApiErrorWrapper(datasetError.getMatchList())
  public async getDatasetMatchesAsync(
    filter: DatasetMatchListFilter
  ): Promise<PageRes<DatasetMatch>> {
    const response: IPageRes<IDatasetMatch> = await apiDefinition.getDatasetMatchesListAsync(
      filter.types,
      filter.userActionNeeded,
      filter.referenceId,
      filter.includeOldMatches,
      filter.userActionResolution,
      filter.matchRelevance,
      filter.entityType,
      filter.entityId,
      filter.entityName,
      filter.entityNpi,
      filter.entityDeactivated,
      filter.credentialingStatus,
      filter.sortedBy,
      filter.sort,
      filter.cursor,
      filter.count,
      filter.sortDirection
    );
    return PageRes.create<DatasetMatch, IDatasetMatch>(
      response,
      (match: IDatasetMatch) => new DatasetMatch(match)
    );
  }

  @ApiErrorWrapper(datasetError.getScan())
  public async getDatasetScanAsync(scanId: string): Promise<DatasetScan> {
    return new DatasetScan(await apiDefinition.getDatasetScanAsync(scanId));
  }

  @ApiErrorWrapper(datasetError.startScan())
  public async startDatasetScanAsync(payload: IStartDatasetScan): Promise<DatasetScan> {
    return new DatasetScan(await apiDefinition.startDatasetScanAsync(payload));
  }

  @ApiErrorWrapper(datasetError.refreshScan())
  public async refreshDatasetScanAsync(scanId: string): Promise<void> {
    await apiDefinition.refreshDatasetScanAsync(scanId);
  }

  @ApiErrorWrapper(datasetError.patchMatch())
  public async patchDatasetMatchAsync(
    matchId: string,
    payload: IDatasetMatchPatchRequest
  ): Promise<DatasetMatch> {
    return new DatasetMatch(await apiDefinition.patchDatasetMatchAsync(matchId, payload));
  }

  @ApiErrorWrapper(datasetError.getMetadata())
  public async getDatasetMetadataAsync(type: string): Promise<DatasetMeta> {
    return new DatasetMeta(await apiDefinition.getDatasetMetadataAsync(type));
  }

  @ApiErrorWrapper(datasetError.getMetadataList())
  public async getDatasetMetaListAsync(
    filter: DatasetMetaListFilter
  ): Promise<PageRes<DatasetMeta>> {
    const response: IPageRes<IDatasetMeta> = await apiDefinition.getDatasetMetaListAsync(
      filter.tags,
      filter.entityTypes,
      filter.cursor,
      filter.count,
      filter.sortedBy,
      filter.sortDirection
    );

    return PageRes.create<DatasetMeta, IDatasetMeta>(
      response,
      (meta: IDatasetMeta) => new DatasetMeta(meta)
    );
  }

  @ApiErrorWrapper()
  public async uploadDatasetFileAsync(datasetType: string, formData: FormData): Promise<void> {
    return apiDefinition.uploadDatasetFileAsync(datasetType, formData);
  }
  //#endregion

  //#region Verification meta datas
  public async getVerificationMetaDataAsync(): Promise<VerificationMetadata> {
    const response: IVerificationMetadata = await apiDefinition.getVerificationMetaDataAsync();
    return new VerificationMetadata(response);
  }
  //#endregion

  //#region Not documented APIs
  @ApiErrorWrapper(organizationError.getSettings())
  public async getOrgSettingsAsync(): Promise<OrgSettings | undefined> {
    const orgSettings: IOrgSettings = await apiDefinition.getOrgSettingsAsync();
    if (!orgSettings) return;

    return new OrgSettings(orgSettings.organizationId, orgSettings.settings);
  }

  @ApiErrorWrapper(organizationError.setSettings())
  public async setOrganizationSettingsAsync<T>(setting: string, settingData: T): Promise<void> {
    await apiDefinition.setOrganizationSettingsAsync<T>(setting, settingData);
  }

  @ApiErrorWrapper(organizationError.getSettings())
  public async getOrganizationSettingAsync<T, R>(
    setting: Settings,
    includeSensitiveInfo = false
  ): Promise<T | undefined> {
    const settingRes = await apiDefinition.getOrganizationSettingAsync<R>(
      setting,
      includeSensitiveInfo
    );
    return SettingsFactory.get<T>(setting, settingRes);
  }

  @ApiErrorWrapper(organizationError.getSettings())
  public async getOAuthCredentialsAsync(): Promise<IOAuthCredentialsModel> {
    const credentials: IOAuthCredentialsModel[] = await apiDefinition.getOAuthCredentialsAsync();

    return credentials?.first();
  }

  @ApiErrorWrapper(organizationError.modifyOAuthClientSecrets())
  public async addOAuthClientSecretAsync(clientId: string): Promise<IClientSecretModel> {
    return await apiDefinition.addClientSecretAsync(clientId);
  }

  @ApiErrorWrapper(organizationError.modifyOAuthClientSecrets())
  public async deleteOAuthClientSecretAsync(
    clientId: string,
    clientSecretId: string
  ): Promise<void> {
    await apiDefinition.deleteClientSecretAsync(clientId, clientSecretId);
  }

  @ApiErrorWrapper(organizationError.getSettings())
  public async getSsoAdminPortalAsync(): Promise<string> {
    return await apiDefinition.getSsoAdminPortalAsync();
  }

  //#endregion

  //#region Schemas
  @ApiErrorWrapper(schemaError.getSchema())
  public async getSchemaMetadataAsync(enumName: string): Promise<SchemaMeta[]> {
    return (
      (await apiDefinition.getSchemaMetadataAsync(enumName))?.map(
        (schema: ISchemaMeta) => new SchemaMeta(schema)
      ) || []
    );
  }
  //#endregion

  //#region Files
  @ApiErrorWrapper(fileError.getList())
  public async getFileListAsync(filter: FileListFilter): Promise<PageRes<XFile>> {
    const response: IPageRes<IFile> = await apiDefinition.getFileListAsync(
      filter.prefix,
      filter.cursor,
      filter.count,
      filter.sortedBy,
      filter.sortDirection
    );

    return PageRes.create<XFile, IFile>(response, (file: IFile) => new XFile(file));
  }

  @ApiErrorWrapper(fileError.delete())
  public async deleteFileAsync(path: string): Promise<void> {
    await apiDefinition.deleteFileAsync(path);
  }

  @ApiErrorWrapper(fileError.overwriteFile())
  public async renameFileAsync(path: string, oldPath: string, isMove: boolean): Promise<void> {
    await apiDefinition.renameFileAsync(path, oldPath, isMove);
  }

  /**
   * Downloads a file from the server with no-cache behavior
   * Forces the browser to revalidate with the server before using any cached response
   * @param path - The file path to download
   * @returns A Blob containing the file data
   */
  @ApiErrorWrapper(fileError.download())
  public async getFileAsync(path: string): Promise<Blob> {
    return await apiDefinition.getFileAsync(path, CacheControl.NoCache);
  }

  @ApiErrorWrapper(fileError.upload())
  public async uploadFileAsync(
    path: string,
    formData: FormData,
    uploadCallback: (
      completed: number,
      total: number,
      requestFinished: boolean,
      error: ApiError
    ) => void
  ): Promise<Blob> {
    return await apiDefinition.uploadFileAsync(path, formData, uploadCallback);
  }
  //#endregion

  //#region Monitoring
  @ApiErrorWrapper(monitorError.create())
  public async createMonitoringAsync(payload: IMonitorReq): Promise<Monitor> {
    return MonitorFactory.get(await apiDefinition.createMonitoringAsync(payload));
  }

  @ApiErrorWrapper(monitorError.getList())
  public async getMonitoringListAsync(filter: MonitorListFilter): Promise<PageRes<Monitor>> {
    const response: IPageRes<IMonitor> = await apiDefinition.getMonitoringListAsync(
      filter.entityIds,
      filter.entityType as string,
      filter.type,
      filter.datasetTypes,
      filter.licenseIds,
      filter.sortedBy,
      filter.cursor,
      filter.count,
      filter.sortDirection
    );

    return PageRes.create<Monitor, IMonitor>(response, (monitor: IMonitor) =>
      MonitorFactory.get(monitor)
    );
  }

  @ApiErrorWrapper(monitorError.get())
  public async getMonitoringAsync(id: string): Promise<Monitor> {
    return MonitorFactory.get(await apiDefinition.getMonitoringAsync(id));
  }

  @ApiErrorWrapper(monitorError.patch())
  public async patchMonitoringAsync(id: string, payload: IMonitorPatchReq): Promise<Monitor> {
    return MonitorFactory.get(await apiDefinition.patchMonitoringAsync(id, payload));
  }

  @ApiErrorWrapper(monitorError.delete())
  public async deleteMonitoringAsync(id: string): Promise<void> {
    await apiDefinition.deleteMonitoringAsync(id);
  }
  //#endregion

  //#region Credentialing Request Aggregation
  @ApiErrorWrapper()
  public async getCredentialingReqAggregationAsync(
    entityType: ModuleEntityType,
    entityDeactivated: boolean | null = null
  ): Promise<CvoAggregation> {
    return CvoAggregation.create(
      await apiDefinition.getCredentialingReqAggregationAsync(entityType, entityDeactivated)
    );
  }
  //#endregion

  //#region AuditLog
  @ApiErrorWrapper(auditError.getList())
  public async getAuditLogsAsync(filter: AuditLogListFilter): Promise<PageRes<AuditLog>> {
    const response: IPageRes<IAuditLog> = await apiDefinition.getAuditLogsAsync(
      filter.entityId,
      filter.beforeDateTime,
      filter.afterDateTime,
      filter.beforeInclusive,
      filter.afterInclusive,
      filter.type,
      filter.category,
      filter.subCategory,
      filter.sortDirection,
      filter.cursor,
      filter.count,
      filter.sortedBy,
      filter.sort,
      filter.includeFailedRequests
    );

    return PageRes.create<AuditLog, IAuditLog>(response, (log: IAuditLog) => new AuditLog(log));
  }

  @ApiErrorWrapper(auditError.getExportList())
  public async getDownloadLogs(filter: DownloadLogsFilter): Promise<PageRes<DownloadLogs>> {
    const response: IPageRes<IDownloadLog> = await apiDefinition.getDownloadLogs(
      filter.prefix,
      filter.beforeDateTime,
      filter.afterDateTime,
      filter.beforeInclusive,
      filter.afterInclusive,
      filter.sortDirection,
      filter.cursor,
      filter.count,
      filter.sortedBy
    );

    return PageRes.create<DownloadLogs, IDownloadLog>(
      response,
      (log: IDownloadLog) => new DownloadLogs(log)
    );
  }
  //#endregion

  //#region License Source
  public async getLicenseSourceStatusAsync(
    filter: LicenseTypeStatusListFilter
  ): Promise<PageRes<SimplifiedLicenseTypeStatusModel>> {
    const response: IPageRes<ISimplifiedLicenseTypeStatus> =
      await apiDefinition.getLicenseSourceStatusAsync(
        filter.licenseTypeId,
        filter.state,
        filter.licenseTypeName,
        filter.sourceIssue,
        filter.cursor,
        filter.count,
        filter.sortedBy,
        filter.sortDirection
      );

    return PageRes.create<SimplifiedLicenseTypeStatusModel, ISimplifiedLicenseTypeStatus>(
      response,
      (item: ISimplifiedLicenseTypeStatus) => new SimplifiedLicenseTypeStatusModel(item)
    );
  }
  //#endregion

  //#region Taxonomies
  @ApiErrorWrapper()
  public async getTaxonomyGroupsAsync(
    filter: PageFilter<TaxonomyGroupSort>
  ): Promise<PageRes<TaxonomyGroup>> {
    const response: IPageRes<ITaxonomyGroup> = await apiDefinition.getTaxonomyGroupsAsync(
      filter.sortedBy,
      filter.cursor,
      filter.count,
      filter.sortDirection,
      filter.sort
    );

    return PageRes.create<TaxonomyGroup, ITaxonomyGroup>(
      response,
      (item: ITaxonomyGroup) => new TaxonomyGroup(item)
    );
  }

  @ApiErrorWrapper()
  public async getTaxonomiesAsync(
    filter: PageFilter<TaxonomySort>
  ): Promise<PageRes<TaxonomyModel>> {
    const response: IPageRes<ITaxonomy> = await apiDefinition.getTaxonomiesAsync(
      filter.sortedBy,
      filter.cursor,
      filter.count,
      filter.sortDirection,
      filter.sort
    );

    return PageRes.create<TaxonomyModel, ITaxonomy>(
      response,
      (item: ITaxonomy) => new TaxonomyModel(item)
    );
  }
  //#endregion
}

export const api = new Api();
